import { useList, useGo, useDelete } from "@refinedev/core";
import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormHeader } from "@/components/forms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { useScrollRestoration } from "@/hooks/use-scroll-restoration";

type Language = {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji?: string;
  flag?: string;
  isDefault?: boolean;
};

type LanguageWithId = Language & { id: string };

/**
 * LanguagesList
 * 
 * Список languages с фильтрацией и управлением
 * 
 * @returns {React.ReactElement} React компонент
 */
export function LanguagesList() {
  const go = useGo();
  useScrollRestoration();
  const [page, setPage] = usePersistedState("languages:page", 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>("languages:sort", { field: "name", order: "asc" });
  const [deleteItems, setDeleteItems] = useState<LanguageWithId[]>([]);
  const [search, setSearch] = usePersistedState("languages:search", "");
  const [isActiveFilter, setIsActiveFilter] = usePersistedState<string>("languages:isActiveFilter", "ALL");
  const pageSize = 20;

  const listResult = useList<Language>({
    resource: "languages",
    pagination: { currentPage: page, pageSize, mode: "server" },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters: [
      ...(search ? [{ field: "search", operator: "contains" as const, value: search }] : []),
      ...(isActiveFilter !== "ALL" ? [{ field: "isActive", operator: "eq" as const, value: isActiveFilter === "ACTIVE" }] : []),
    ],
  });

  const languages = listResult.result?.data ?? [];
  const total = listResult.result?.total ?? 0;
  const isLoading = listResult.query.isLoading;

  const { mutate: deleteLanguage, mutation: deleteMutation } = useDelete();
  const isDeleting = deleteMutation.status === "pending";

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deleteLanguage({
        resource: "admin/languages",
        id: item.code,
      });
    }
    setDeleteItems([]);
  };

  const columns: Column<LanguageWithId>[] = [
    {
      key: "code",
      header: "Код",
      width: "80px",
      sortable: true,
      render: (value, language) => (
        <div className="flex items-center gap-2">
          <span className="text-xl">
            {language.flagEmoji || language.flag || "🏳️"}
          </span>
          <code className="text-sm font-mono bg-muted px-1 rounded">
            {String(value)}
          </code>
        </div>
      ),
    },
    {
      key: "name",
      header: "Название",
      sortable: true,
      render: (value, language) => (
        <div className="flex flex-col">
          <span className="font-medium">{String(value)}</span>
          <span className="text-xs text-muted-foreground">
            {language.nativeName}
          </span>
        </div>
      ),
    },
  ];

  const dataWithIds = languages.map((l) => ({ ...l, id: l.code }));

  const bulkActions: BulkAction<LanguageWithId>[] = [
    {
      label: "Редактировать",
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/languages/edit/${items[0].code}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Языки"
        description="Управление доступными языками системы"
        actions={
          <div className="flex gap-2">
            <Button onClick={() => go({ to: "/languages/create" })}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить язык
            </Button>
          </div>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder="Поиск..."
        filtersCount={isActiveFilter !== "ALL" ? 1 : 0}
        onResetFilters={() => { setIsActiveFilter("ALL"); setPage(1); }}
        filters={
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Статус активности</span>
            <Select value={isActiveFilter} onValueChange={(v) => { setIsActiveFilter(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Все" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Все</SelectItem>
                <SelectItem value="ACTIVE">Активные</SelectItem>
                <SelectItem value="INACTIVE">Неактивные</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={dataWithIds}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(language) =>
          go({ to: `/languages/edit/${language.code}` })
        }
        emptyMessage="Нет добавленных языков"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog
        open={deleteItems.length > 0}
        onOpenChange={() => setDeleteItems([])}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить язык?"
                : `Удалить ${deleteItems.length} языков?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить язык «{deleteItems[0].name}»?
                  Это может повлиять на существующие переводы.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} языков?
                  Это может повлиять на существующие переводы.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
